import moment from 'moment-timezone';
import { Segment, Timezone } from '@fxtr/i18n';
import { penceToPounds } from '$app/utils/util';

const updateTripsWithPickerNames = (trips = [], heroes = [], segment) => {
  const jsTrips = trips.map((trip) => ({ ...trip }));
  const getPickerName = (id) => {
    const hero = heroes.find((h) => h.driverId === id);
    if (!hero) return id;
    return `${id} ${hero.fullName}`;
  };
  const enhanceTrip = (trip) =>
    Object.assign(trip, {
      pickerName: getPickerName(trip.pickerId),
      leg1: trip.legs.find((leg) => leg.direction === 'inbound') || {},
      leg2: trip.legs.find((leg) => leg.direction === 'outbound') || {},
      segment,
    });
  const maybeEnhanceTrip = (trip) => (trip.pickerId ? enhanceTrip(trip) : trip);
  return jsTrips.map(maybeEnhanceTrip);
};

const extractAssignHeroesTableData = (hero) => {
  const defaultRate = (hero.rates || []).find((r) => r.rateType === 'default');
  const driverRate = defaultRate ? penceToPounds(defaultRate.pence) : '-';
  return {
    key: hero.driverId,
    name: hero.fullName,
    email: hero.emailAddress,
    distance: hero.proximity,
    driverUuid: hero.driverUuid,
    rate: driverRate,
    distanceBasedPricing: hero.distanceBasedPricing,
    dbpRateTier1: hero.dbpRateTier1,
    dbpRateTier2: hero.dbpRateTier2,
    dbpRateTier3: hero.dbpRateTier3,
    lastActiveSince: hero.lastActiveSince,
  };
};

const lastFinishedLeg = (trip) => {
  if (!trip.legs || !trip.legs.length) return null;
  let leg1, lastLeg, legInfo;

  const leg2 = trip.legs.find((leg) => leg.direction === 'outbound');

  if (leg2) {
    if (leg2.end) {
      lastLeg = leg2.end;
      legInfo = 'Leg2 ended';
    } else if (leg2.start) {
      lastLeg = leg2.start;
      legInfo = 'Leg2 started';
    } else {
      leg1 = trip.legs.find((leg) => leg.direction === 'inbound');
    }
  }

  if (leg1) {
    if (leg1.end) {
      lastLeg = leg1.end;
      legInfo = 'Leg1 ended';
    } else if (leg1.start) {
      lastLeg = leg1.start;
      legInfo = 'Leg1 started';
    }
  }
  if (!lastLeg) return ['No legs started'];
  const timezone = trip.segment === Segment.Fixter_FR ? Timezone['Europe/Paris'] : Timezone['Europe/London'];
  return [legInfo, moment(lastLeg).tz(timezone).format('LLL')];
};

const extractHeroTripsTableData = (trip) => ({
  key: trip.tripId,
  tripLeg: trip.type,
  heroId: trip.pickerName || 'not assigned',
  lastLeg: lastFinishedLeg(trip),
  status: trip.status,
  action: 'pick hero btn',
  attachments: 'wip',
  failedReason: trip.failedReason || null,
  allocationStatus: trip.allocationStatus || 'unknown',
});

const sortHeroesByDriverId = (pickers = []) => [...pickers].sort((a, b) => a.driverId - b.driverId);
const pickerName = (picker) => `${picker.firstName} ${picker.lastName}`;

export const normalizeHeroes = (heroes = []) =>
  sortHeroesByDriverId(heroes.map((picker) => ({ ...picker, fullName: pickerName(picker) })));

export const getBookingLatLng = (booking = {}) => ({
  latitude: booking.collectionAddress?.latitude,
  longitude: booking.collectionAddress?.longitude,
});

export const buildTripsTableData = (booking = {}, heroes = []) => {
  const tripsWithHeroNames = updateTripsWithPickerNames(booking.trips || [], heroes, booking.segment);
  return tripsWithHeroNames.sort((a, b) => a.tripId - b.tripId).map(extractHeroTripsTableData);
};

export const buildAssignHeroesTableData = (heroes = []) =>
  heroes.filter((hero) => hero.status === 'active').map(extractAssignHeroesTableData);
